// Medical funding — PI Platform outline v2 §1.5.
//
// On the Providers tab, with the treatment it pays for. This card
// answers two questions the rest of the app cannot:
//
//   Who is paying for this treatment?
//   What happens when they stop?
//
// The second is the one worth building for. An exhausted MedPay pot
// found at disbursement is a surprise; found during treatment it is a
// decision — move the client to an LOP, or stop the bleeding. So the
// remaining balance is the most prominent thing on each row, and the
// warning arrives before the pot is empty rather than after.
//
// LOP signatures are shown as three separate marks rather than a single
// "signed" state, because an LOP missing one is unenforceable and the
// missing one is the actionable detail.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const SOURCE_LABELS = {
  medpay: "MedPay", pip: "PIP", health_insurance: "Health insurance",
  lop: "Letter of protection", self_pay: "Self-pay", medicaid: "Medicaid",
  medicare: "Medicare", workers_comp: "Workers' comp", va: "VA", other: "Other",
};

const PARTY_LABELS = { client: "Client", attorney: "Attorney", provider: "Provider" };

const money = (v) =>
  v === null || v === undefined ? "—" : `$${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EMPTY_SOURCE = { source_type: "medpay", name: "", limit_amount: "" };

export function FundingCard({ matterId, showNotification }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(EMPTY_SOURCE);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`/api/matters/${matterId}/funding`));
    } catch (e) {
      showNotification?.(`Funding failed to load: ${e.message}`, "error");
      setData({ sources: [], lops: [], summary: null });
    }
  }, [matterId, showNotification]);

  useEffect(() => { load(); }, [load]);

  const addSource = async () => {
    if (!draft.name.trim() && !draft.limit_amount) {
      showNotification?.("Give it a name or a limit so it means something.", "warning");
      return;
    }
    try {
      await apiFetch(`/api/matters/${matterId}/funding-sources`, {
        method: "POST",
        body: JSON.stringify({
          source_type: draft.source_type,
          name: draft.name.trim() || null,
          limit_amount: draft.limit_amount === "" ? null : Number(draft.limit_amount),
        }),
      });
      setDraft(EMPTY_SOURCE); setAdding(false);
      await load();
    } catch (e) { showNotification?.(`Couldn't add it: ${e.message}`, "error"); }
  };

  const recordDraw = async (src) => {
    const amount = window.prompt(`Amount billed against ${src.name || SOURCE_LABELS[src.source_type]}:`);
    if (!amount) return;
    const provider = window.prompt("Which provider? (attribution is what makes exhaustion actionable)");
    try {
      const r = await apiFetch(`/api/funding-sources/${src.id}/draws`, {
        method: "POST",
        body: JSON.stringify({ amount: Number(amount), provider_name: provider || null }),
      });
      if (r.is_exhausted) {
        showNotification?.(
          `${r.name || SOURCE_LABELS[r.source_type]} is now exhausted`
          + (r.unfunded ? ` — ${money(r.unfunded)} of treatment is beyond it.` : "."),
          "warning");
      }
      await load();
    } catch (e) { showNotification?.(`Failed: ${e.message}`, "error"); }
  };

  const addLop = async () => {
    const provider = window.prompt("Which provider is the LOP with?");
    if (!provider) return;
    const cap = window.prompt("Cap on the provider's recovery, if the LOP has one (blank if not):");
    try {
      await apiFetch(`/api/matters/${matterId}/lops`, {
        method: "POST",
        body: JSON.stringify({
          provider_name: provider,
          amount_cap: cap ? Number(cap) : null,
        }),
      });
      await load();
    } catch (e) { showNotification?.(`Couldn't create it: ${e.message}`, "error"); }
  };

  const sign = async (lop, party) => {
    try {
      await apiFetch(`/api/lops/${lop.id}/signatures`, {
        method: "POST", body: JSON.stringify({ party }),
      });
      await load();
    } catch (e) { showNotification?.(`Failed: ${e.message}`, "error"); }
  };

  const execute = async (lop) => {
    try {
      const r = await apiFetch(`/api/lops/${lop.id}/execute`, { method: "POST" });
      showNotification?.(
        `Executed — a lien for ${r.provider_name} is now on the settlement.`, "success");
      await load();
    } catch (e) {
      // The unsigned refusal explains itself; show it whole.
      showNotification?.(e.message, "error");
    }
  };

  if (data === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
          "Loading funding…")));
  }

  const { sources, lops, summary } = data;
  const inp = { padding: "7px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                fontSize: "13px", background: "#fff" };

  return React.createElement("div", { style: cardStyle },
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" },
    },
      React.createElement("div", null,
        React.createElement("h2", { style: h2Style }, "Medical Funding"),
        React.createElement("div", { style: { fontSize: "13px", color: C.textMuted, marginTop: "4px" } },
          sources.length === 0 && lops.length === 0
            ? "Nothing recorded — who is paying for this treatment?"
            : [
                sources.length ? `${sources.length} funding source${sources.length === 1 ? "" : "s"}` : null,
                lops.length ? `${lops.length} LOP${lops.length === 1 ? "" : "s"}` : null,
                summary?.total_unfunded ? `${money(summary.total_unfunded)} unfunded` : null,
              ].filter(Boolean).join(" · ")),
      ),
      React.createElement("div", { style: { display: "flex", gap: "8px", flexShrink: 0 } },
        _outline(adding ? "Cancel" : "Add source", () => setAdding(a => !a)),
        _outline("Add LOP", addLop),
      ),
    ),

    adding && React.createElement("div", {
      style: { display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center",
               padding: "12px 14px", background: C.warmWhite, borderRadius: "8px", marginTop: "12px" },
    },
      React.createElement("select", {
        value: draft.source_type, style: { ...inp, minWidth: "170px" },
        onChange: e => setDraft({ ...draft, source_type: e.target.value }),
      }, ...Object.entries(SOURCE_LABELS).map(([v, l]) =>
        React.createElement("option", { key: v, value: v }, l))),
      React.createElement("input", {
        type: "text", value: draft.name, placeholder: "Carrier or plan",
        onChange: e => setDraft({ ...draft, name: e.target.value }),
        style: { ...inp, flex: "1 1 180px" },
      }),
      React.createElement("input", {
        type: "number", value: draft.limit_amount, placeholder: "Limit (MedPay/PIP)",
        onChange: e => setDraft({ ...draft, limit_amount: e.target.value }),
        style: { ...inp, width: "170px" },
      }),
      React.createElement("button", {
        onClick: addSource,
        style: { padding: "8px 16px", borderRadius: "8px", border: "none", background: C.gold,
                 color: "#fff", fontSize: "12px", fontWeight: 700, cursor: "pointer" },
      }, "Add"),
    ),

    // ── Funding sources ─────────────────────────────────────
    sources.length > 0 && React.createElement("div", {
      style: { border: `1px solid ${C.warmGray}60`, borderRadius: "8px",
               overflow: "hidden", marginTop: "14px" },
    },
      ...sources.map((s, i) => React.createElement("div", {
        key: s.id,
        style: { padding: "11px 13px", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}40`,
                 display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
                 background: s.is_exhausted ? C.warmWhite : "#fff" },
      },
        React.createElement("div", { style: { flex: "1 1 190px", minWidth: 0 } },
          React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
            s.name || SOURCE_LABELS[s.source_type] || s.source_type),
          React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } },
            SOURCE_LABELS[s.source_type] || s.source_type,
            s.providers.length ? ` · ${s.providers.join(", ")}` : "")),

        // Remaining is the headline for an exhaustible pot; a
        // non-exhaustible one just shows what has been drawn.
        React.createElement("div", { style: { textAlign: "right", whiteSpace: "nowrap" } },
          s.is_exhaustible
            ? React.createElement("div", null,
                React.createElement("div", { style: { fontSize: "14px", fontWeight: 700, color: C.navy } },
                  s.remaining !== null && s.remaining < 0
                    ? `${money(0)} left`
                    : `${money(s.remaining)} left`),
                React.createElement("div", { style: { fontSize: "11px", color: C.textMuted } },
                  `${money(s.drawn)} of ${money(s.limit_amount)} used`))
            : React.createElement("div", { style: { fontSize: "13px", color: C.textDark } },
                `${money(s.drawn)} billed`)),

        s.unfunded ? React.createElement("span", {
          title: "Billed treatment beyond this pot — it arrives at settlement as a lien or a client balance.",
          style: { fontSize: "11px", fontWeight: 700, color: C.navy, background: `${C.gold}20`,
                   padding: "3px 9px", borderRadius: "9999px", whiteSpace: "nowrap" },
        }, `${money(s.unfunded)} unfunded`) : null,

        _quiet("Record charge", () => recordDraw(s)),
      )),
    ),

    // ── Letters of protection ───────────────────────────────
    lops.length > 0 && React.createElement("div", { style: { marginTop: "16px" } },
      React.createElement("h3", { style: h3Style }, "Letters of protection"),
      React.createElement("div", {
        style: { border: `1px solid ${C.warmGray}60`, borderRadius: "8px",
                 overflow: "hidden", marginTop: "8px" },
      },
        ...lops.map((l, i) => React.createElement("div", {
          key: l.id,
          style: { padding: "11px 13px", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}40`,
                   display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },
        },
          React.createElement("div", { style: { flex: "1 1 180px", minWidth: 0 } },
            React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
              l.provider_name),
            React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } },
              l.amount_cap ? `capped at ${money(l.amount_cap)}` : "no cap",
              l.lien_id ? " · lien created" : "")),

          // Three marks, not one. The missing signature is the
          // actionable detail.
          React.createElement("div", { style: { display: "flex", gap: "6px", flexWrap: "wrap" } },
            ...["client", "attorney", "provider"].map(party =>
              React.createElement("button", {
                key: party,
                onClick: () => l.signatures[party] ? null : sign(l, party),
                disabled: l.signatures[party],
                title: l.signatures[party]
                  ? `${PARTY_LABELS[party]} has signed`
                  : `Record ${PARTY_LABELS[party].toLowerCase()} signature`,
                style: {
                  border: `1px solid ${l.signatures[party] ? `${C.gold}50` : C.warmGray}`,
                  background: l.signatures[party] ? `${C.gold}18` : "#fff",
                  color: l.signatures[party] ? C.navy : C.textMuted,
                  borderRadius: "6px", padding: "4px 10px", fontSize: "11px",
                  fontWeight: 600, cursor: l.signatures[party] ? "default" : "pointer",
                },
              }, PARTY_LABELS[party] + (l.signatures[party] ? " ✓" : "")))),

          l.status !== "executed" && _quiet(
            l.is_fully_signed ? "Execute" : "Execute", () => execute(l)),
        )),
      ),
    ),

    summary && (summary.warnings || []).length > 0 && React.createElement("ul", {
      style: { margin: "14px 0 0", paddingLeft: "18px", fontSize: "12px",
               color: C.textMuted, lineHeight: 1.6 },
    }, ...summary.warnings.map((w, i) => React.createElement("li", { key: i }, w))),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};
const h3Style = {
  fontSize: "12px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.06em", fontWeight: 600, margin: 0,
};

function _quiet(label, onClick) {
  return React.createElement("button", {
    key: label, onClick,
    style: {
      border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
      padding: "5px 11px", fontSize: "11px", fontWeight: 600,
      color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
    },
  }, label);
}

function _outline(label, onClick) {
  return React.createElement("button", {
    key: label, onClick,
    style: {
      padding: "7px 14px", borderRadius: "8px", border: "1px solid #1B2A4A",
      background: "transparent", color: "#1B2A4A", fontSize: "12px",
      fontWeight: 600, cursor: "pointer",
    },
  }, label);
}
